import { FetchModel, LoadObjectResult } from "models/FetchModel";
import type { FetchWithTimeoutOptions } from "services/fetchWithTImeout";

type Body = Pick<RequestInit, 'body'>
type ObjectResponse<T> = LoadObjectResult<T>

type QueryParams = Record<string, string | number>

const fetchModelInstance = FetchModel.create()

class Api {
  private baseUrl: string;
  private options: RequestInit;

  constructor(baseUrl: string, options: RequestInit = {}) {
    this.baseUrl = baseUrl;
    this.options = options;
  }

  private perform<Payload, ResponseType>(
    action: string,
    uri: string,
    body?: Payload,
    queryParams?: QueryParams,
  ): Promise<ObjectResponse<ResponseType>> {
    const requestOptions: RequestInit = {
      ...this.options,
      method: action,
      headers: {
        ...this.options.headers,
        ...(action !== 'GET' && { 'Content-Type': 'application/json' }),
      },
    };

    if (action !== 'GET') {
      requestOptions.body = JSON.stringify(body);
    }

    if (queryParams) {
      /* 
        Convert numbers to strings
        https://github.com/microsoft/TypeScript-DOM-lib-generator/issues/1568 
      */
      const stringifiedQueryParams = Object.fromEntries(
        Object.entries(queryParams).map(([key, value]) => [key, String(value)])
      )

      uri += '?' + new URLSearchParams(stringifiedQueryParams).toString();
    }

    return fetchModelInstance.loadObjectOrError<ResponseType>(this.baseUrl + uri, requestOptions, { fetchOperationName: 'Api Request' })
  }

  get<Response>(uri: string, params?: QueryParams): Promise<ObjectResponse<Response>> {
    return this.perform<Body, Response>('GET', uri, {}, params);
  }

  post<P = never, R = never>(uri: string, body?: P, params?: QueryParams): Promise<ObjectResponse<R>> {
    return this.perform<P, R>('POST', uri, body, params);
  }

  put<P = never, R = never>(uri: string, body?: P, params?: QueryParams): Promise<ObjectResponse<R>> {
    return this.perform<P, R>('PUT', uri, body, params);
  }

  patch<P = never, R = never>(uri: string, body?: P, params?: QueryParams): Promise<ObjectResponse<R>> {
    return this.perform<P, R>('PATCH', uri, body, params);
  }

  delete<P = never, R = never>(uri: string, body?: P, params?: QueryParams): Promise<ObjectResponse<R>> {
    return this.perform<P, R>('DELETE', uri, body, params);
  }

  directFetch(uri: string, options: FetchWithTimeoutOptions): ReturnType<typeof fetchModelInstance.fetch> {
    return fetchModelInstance.fetch(this.baseUrl + uri, options)
  }
}

export default Api;